import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdInput,
  CmdLabel,
  CmdModal,
  CmdModalFooterButton,
  CmdSimpleSelect,
  CmdTextarea,
} from '@commander-services/gui-components';
import { useSetRecoilState } from 'recoil';
import { Controller, useForm } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import FormikValidatorService, {
  IValidationApiResponse,
} from '../../Services/FormikValidatorService';
import PermissionsService, { PERMISSION_COST_CENTERS } from '../../Services/PermissionsService';
import { IOption } from '../Forms/CmdField';
import usePageWithForm from '../../hooks/usePageWithForm';
import { setLoaderAtom } from '../../store/recoil/AppState';
import { transformErrors, validateFields } from '../../Services/HookFormService';
import useAnalytics from '../../hooks/useAnalytics';
import { getRequest, postRequest, putRequest } from '../../Services/HttpService';
import { TABLE_COST_CENTERS } from '../../Services/TableService';

interface ModalFormProps {
  handleClose: () => void;
  id: number | null;
  updateTable?: (() => void) | undefined;
  customerId?: number | null;
}
type TCostCentersFormKeys = 'customerId' | 'name' | 'note';

type CostCentersFormValues = {
  customerId: string;
  name: string;
  note: string;
};

export default function CostCentersForm(props: ModalFormProps) {
  usePageWithForm();
  const { trackEvent } = useAnalytics();
  const { formatMessage: f } = useIntl();
  const setLoader = useSetRecoilState<boolean>(setLoaderAtom);
  const [validation, setValidation] = React.useState<IValidationApiResponse | null>(null);
  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors },
  } = useForm<CostCentersFormValues>({
    mode: 'onChange',
    defaultValues: {
      customerId: '',
      name: '',
      note: '',
    },
    resolver: async (data) => {
      const validateErros = validateFields(data, validation);
      const formErrors = transformErrors(validateErros);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const tableName = TABLE_COST_CENTERS;

  const onSubmit = async (values: CostCentersFormValues) => {
    const processedValues = {
      ...values,
      customerId: Number(values.customerId),
    };

    if (props.id) {
      const url = `/v1/${tableName}/${props.id}`;
      setLoader(true);

      const response = await putRequest(url, processedValues);

      if (response && props.updateTable) {
        trackEvent(['CostCenters form', 'submit', 'Update']);
        props.updateTable();
        props.handleClose();
      }

      setLoader(false);
    } else {
      const url = `/v1/${tableName}/add`;
      setLoader(true);

      const response = await postRequest(url, processedValues);

      if (response && props.updateTable) {
        trackEvent(['CostCenters form', 'submit', 'Create']);
        props.updateTable();
        props.handleClose();
      }

      setLoader(false);
    }
  };

  const getValidation = async () => {
    const url = `/v1/${tableName}/validation`;
    setLoader(true);
    const response = await getRequest<IValidationApiResponse>(url);

    if (response) {
      setValidation(response as IValidationApiResponse);
    }
    setLoader(false);
  };

  const getData = async () => {
    const url = `/v1/${tableName}/${props.id}${
      props.customerId ? `?customerId=${props.customerId}` : ''
    }`;
    setLoader(true);

    const response: AxiosResponse | false = await getRequest(url, { returnAxiosResponse: true });

    if (response && response.status === 200) {
      const data = response?.data?.costCenter;

      Object.keys(getValues()).forEach((fieldName) => {
        setValue(fieldName as TCostCentersFormKeys, data[fieldName]);
      });
    }

    setLoader(false);
  };

  const getOptionsData = async (name: string) => {
    const url = `/v1/${tableName}/form/${name}`;
    setLoader(true);

    const response: AxiosResponse | false = await getRequest(url, { returnAxiosResponse: true });

    if (response && response.status === 200) {
      return response.data;
    }
    setLoader(false);
    return false;
  };

  const modalButtonsReadOnly: CmdModalFooterButton[] = [
    {
      id: 'costCenter-form-close',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'suppliersForm.close' }),
      tooltip: f({ id: 'alarms.modal.recipient.close.tooltip' }),
      closeCallback: () => {
        trackEvent(['CostCenters form', 'click', 'Close form']);
        props.handleClose();
      },
    },
  ];

  let isAllowed = true;
  if (props.id && props.customerId) {
    isAllowed = PermissionsService.hasAccess(PERMISSION_COST_CENTERS, ['update'], props.customerId);
  }

  const results = useQueries({
    queries: [
      {
        queryKey: ['costCenterFormData'],
        queryFn: () => getData(),
        enabled: !!props.id,
      },
      {
        queryKey: ['costCenterOptionsCustomers'],
        queryFn: () => getOptionsData('customerId'),
      },
      {
        queryKey: ['costCenterFormValidation'],
        queryFn: () => getValidation(),
        enabled: isAllowed,
      },
    ],
  });

  const { isLoading: isLoadingCostCenterData } = results[0];
  const { data: customersOptions, isLoading: isLoadingCostCenterCustomers } = results[1];
  const { isLoading: isLoadingCostCenterValidation } = results[2];

  let customer: IOption | null = null;

  if (customersOptions) {
    customer = customersOptions.find((items: IOption) => items.value === watch('customerId'));
  }

  const modalButtons: CmdModalFooterButton[] = [
    {
      id: 'costCenter-form-close',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'suppliersForm.close' }),
      tooltip: f({ id: 'alarms.modal.recipient.close.tooltip' }),
      closeCallback: () => {
        trackEvent(['CostCenters form', 'click', 'Close form']);
        props.handleClose();
      },
    },
    {
      id: 'costCenter-form-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'suppliersForm.save' }),
      tooltip: f({ id: 'suppliersForm.save' }),
      submitCallback: handleSubmit(onSubmit),
    },
  ];

  React.useEffect(() => {
    if (!props.id && customersOptions && customersOptions.length === 1) {
      setValue('customerId', customersOptions[0]?.value);
    }
  }, [customersOptions, props.id, setValue]);

  const isLoading =
    isLoadingCostCenterData || isLoadingCostCenterCustomers || isLoadingCostCenterValidation;

  return (
    <CmdModal
      id="cost-centers-form"
      title={props.id ? f({ id: 'table.detail' }) : f({ id: 'table.add' })}
      footerButtons={isAllowed ? modalButtons : modalButtonsReadOnly}
      loader={isLoading}
    >
      {isAllowed ? (
        <>
          {(props.id && customer) || (customersOptions && customersOptions.length === 1) ? (
            <CmdLabel
              id="customerId"
              title={f({ id: 'table_list.columns.customer' })}
              value={customer?.item || customersOptions[0]?.item}
              areLabels={true}
            />
          ) : (
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <CmdSimpleSelect
                  {...field}
                  id="customerId"
                  label={f({ id: 'table_list.columns.customer' })}
                  introOption={f({ id: 'form.choose' })}
                  required={FormikValidatorService.isFieldRequired(
                    validation ? validation.customerId : []
                  )}
                  options={customersOptions || []}
                  onChangeCallback={(value) => {
                    trackEvent(['CostCenters form', 'change', 'Customer', Number(value)]);
                    setValue('customerId', value);
                    trigger('customerId');
                  }}
                  error={errors.customerId?.message}
                  touched={true}
                />
              )}
            />
          )}

          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <CmdInput
                {...field}
                label={f({ id: 'table_list.columns.name' })}
                id="name"
                type="text"
                required={FormikValidatorService.isFieldRequired(validation ? validation.name : [])}
                onChangeCallback={(value) => {
                  setValue('name', value);
                  trigger('name');
                }}
                error={errors.name?.message}
                touched={true}
              />
            )}
          />

          <Controller
            name="note"
            control={control}
            render={({ field }) => (
              <CmdTextarea
                {...field}
                id="note"
                label={f({ id: 'table_list.columns.note' })}
                setValue={(value) => {
                  setValue('note', value);
                  trigger('note');
                }}
                required={FormikValidatorService.isFieldRequired(
                  validation && validation.note ? validation.note : []
                )}
                error={errors.note?.message}
                touched={true}
              />
            )}
          />
        </>
      ) : (
        <>
          <CmdLabel
            title={f({ id: 'table_list.columns.customer' })}
            value={customer?.item}
            areLabels={true}
          />
          <CmdLabel
            title={f({ id: 'table_list.columns.name' })}
            value={watch('name')}
            areLabels={true}
          />
          <CmdLabel
            title={f({ id: 'table_list.columns.note' })}
            value={watch('note')}
            areLabels={true}
          />
        </>
      )}
    </CmdModal>
  );
}
